import { useEffect, useState } from "react";
import { UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["Konsult", "Revisor", "Supervisor", "Kvalitetsansvarig", "Byråledning"];
const EMPTY = { name: "", email: "", password: "", role: "Konsult" };

export default function UsersTab({ autoOpenSignal }) {
  const { user } = useAuth();
  const isManager = ["Kvalitetsansvarig", "Byråledning", "Supervisor"].includes(user.role);
  const [users, setUsers] = useState(null);
  const [sub, setSub] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (autoOpenSignal > 0) setModal(true); }, [autoOpenSignal]);

  const cancelSub = async () => {
    if (!window.confirm("Säg upp abonnemanget? Åtkomsten upphör automatiskt efter 1 månads uppsägningstid.")) return;
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success(`Abonnemanget är uppsagt och upphör ${new Date(data.end_date).toLocaleDateString("sv-SE")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reactivateSub = async () => {
    try {
      await api.post("/subscription/reactivate");
      toast.success("Abonnemanget är återaktiverat");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!isManager) return null;
  if (!users) return <div className="text-sm text-slate-500">Laddar...</div>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`Kontot för ${form.name} (${form.role}) har skapats`);
      setModal(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="users-view">
      <div className="flex justify-between items-center border-b border-slate-200 pb-4 mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-slate-900">Användarhantering</h2>
          <p className="text-sm text-slate-500 mt-1">Skapa konton åt anställda. Nya användare måste signera Reko-policyn vid första inloggningen.</p>
        </div>
        <button data-testid="user-add-btn" onClick={() => setModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded shadow flex items-center">
          <UserPlus className="w-3.5 h-3.5 mr-1.5" />Ny användare
        </button>
      </div>

      {sub && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 flex flex-wrap justify-between items-center gap-3" data-testid="subscription-card">
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Prenumeration (årsabonnemang, löpande förnyelse)</div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${sub.status === "AKTIV" ? "bg-emerald-100 text-emerald-800" : sub.status === "UPPSAGD" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`} data-testid="subscription-status">{sub.status}</span>
              {sub.end_date && <span className="text-xs text-slate-600">Upphör: {new Date(sub.end_date).toLocaleDateString("sv-SE")} ({sub.days_left} dagar kvar)</span>}
              {!sub.end_date && sub.start_date && <span className="text-xs text-slate-500">Startad: {new Date(sub.start_date).toLocaleDateString("sv-SE")}</span>}
            </div>
            {sub.status === "UPPSAGD" && <p className="text-[11px] text-amber-700 mt-1">Uppsägningstid 1 månad — inloggningen låses automatiskt för samtliga användare efter slutdatumet.</p>}
          </div>
          <div>
            {sub.status === "AKTIV" ? (
              <button data-testid="subscription-cancel-btn" onClick={cancelSub} className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded shadow">Säg upp abonnemang</button>
            ) : sub.status === "UPPSAGD" ? (
              <button data-testid="subscription-reactivate-btn" onClick={reactivateSub} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded shadow">Återaktivera</button>
            ) : null}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse" data-testid="users-table">
          <thead>
            <tr className="bg-slate-100 text-slate-600 font-bold border-b border-slate-200">
              <th className="p-3">Namn</th><th className="p-3">E-post</th><th className="p-3">Roll</th><th className="p-3">Reko-policy</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="p-3 font-bold text-slate-800">{u.name}</td>
                <td className="p-3 font-mono">{u.email}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded font-bold ${["Kvalitetsansvarig", "Byråledning", "Supervisor"].includes(u.role) ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>{u.role}</span>
                </td>
                <td className="p-3">
                  {u.reko_policy_signed
                    ? <span className="text-emerald-600 font-bold">Signerad {u.reko_signed_at ? new Date(u.reko_signed_at).toLocaleDateString("sv-SE") : ""}</span>
                    : <span className="text-amber-600 font-bold">Ej signerad</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="user-modal">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-3 border border-slate-200">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Skapa användarkonto</h3>
              <button type="button" data-testid="user-modal-close" onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* Namn</label>
                <input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-2 border border-slate-300 rounded" placeholder="För- och efternamn" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* E-postadress</label>
                <input data-testid="user-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full p-2 border border-slate-300 rounded" placeholder="namn@byra.se" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* Lösenord (minst 8 tecken)</label>
                <input data-testid="user-password-input" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block font-semibold text-slate-700 mb-1">* Roll</label>
                <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full p-2 border border-slate-300 rounded">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="text-slate-400 mt-1">Supervisor, Kvalitetsansvarig och Byråledning har full behörighet (kan bl.a. skapa användare och kunder).</p>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-semibold rounded hover:bg-slate-200">Avbryt</button>
              <button data-testid="user-save-btn" type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded hover:bg-blue-700 shadow disabled:opacity-60">
                {saving ? "Skapar..." : "Skapa konto"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
